import React, { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Save, Image as ImageIcon, Plus, Trash2, ArrowUp, ArrowDown, Trophy } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";

const FIELDS = [
  { key: "site_name", label: "Site name", hint: "Shown in header & footer" },
  { key: "hero_eyebrow", label: "Hero eyebrow tag", hint: "Small label above the hero title" },
  { key: "hero_title_1", label: "Hero title (line 1)" },
  { key: "hero_title_2", label: "Hero title (line 2)" },
  { key: "hero_title_3", label: "Hero title (line 3, accent)" },
  { key: "hero_subtitle", label: "Hero subtitle", textarea: true },
  { key: "cta_primary", label: "Primary CTA button text" },
  { key: "cta_secondary", label: "Secondary CTA button text" },
  { key: "footer_tagline", label: "Footer tagline", textarea: true },
];

const BLOCKS = [
  { key: "featured_tournaments", label: "Botões de Torneios em Destaque" },
  { key: "live_ticker", label: "Ticker de Jogos ao Vivo" },
  { key: "upcoming", label: "Próximos Jogos" },
  { key: "latest_results", label: "Resultados Recentes" },
  { key: "top_scorers", label: "Sidebar de Cestinhas" },
  { key: "tournaments_sidebar", label: "Sidebar de Torneios" },
];

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/site/settings").then((r) => setForm(r.data));
  useEffect(() => {
    load();
    api.get("/tournaments?include_archived=true").then((r) => setTournaments(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!form) return;
    setBusy(true);
    try {
      await api.put("/site/settings", form);
      toast.success("Home page updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const setBlock = (key, val) => {
    setForm({ ...form, visible_blocks: { ...(form.visible_blocks || {}), [key]: val } });
  };

  const featured = form?.featured_tournaments || [];
  const setFeatured = (arr) => setForm({ ...form, featured_tournaments: arr.map((f, i) => ({ ...f, order: i })) });
  const addFeatured = () => {
    const usedIds = new Set(featured.map((f) => f.tournament_id));
    const first = tournaments.find((t) => !usedIds.has(t.id) && !t.archived);
    if (!first) { toast.warning("Todos os torneios ativos já estão em destaque"); return; }
    setFeatured([...featured, { tournament_id: first.id, color: "#DC2626", order: featured.length }]);
  };
  const removeFeatured = (idx) => setFeatured(featured.filter((_, i) => i !== idx));
  const moveFeatured = (idx, dir) => {
    const arr = [...featured];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setFeatured(arr);
  };
  const updateFeatured = (idx, patch) => setFeatured(featured.map((f, i) => i === idx ? { ...f, ...patch } : f));

  if (!form) return <div className="p-10 text-ink-muted">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-4xl">
      <PageHeader
        title="Site Settings"
        subtitle="Customize the public Home page texts, images and structure"
        testid="page-settings"
        action={
          <Button
            data-testid="save-settings-btn"
            onClick={save}
            disabled={busy}
            className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm"
          >
            <Save size={16} className="mr-2" /> {busy ? "Saving…" : "Save changes"}
          </Button>
        }
      />

      <div className="bg-navy-deep border border-navy-border rounded-sm p-4 md:p-6 space-y-6">
        <h3 className="font-heading uppercase tracking-widest text-orange text-sm flex items-center gap-2">
          <ImageIcon size={14} /> Background images
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <BackgroundField
            label="Home hero background"
            value={form.hero_bg_url}
            onChange={(v) => setForm({ ...form, hero_bg_url: v })}
            testid="hero-bg"
          />
          <BackgroundField
            label="Login page background"
            value={form.login_bg_url}
            onChange={(v) => setForm({ ...form, login_bg_url: v })}
            testid="login-bg"
          />
        </div>
      </div>

      <div className="bg-navy-deep border border-navy-border rounded-sm p-4 md:p-6 space-y-5 mt-6">
        <h3 className="font-heading uppercase tracking-widest text-orange text-sm">Texts</h3>
        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-ink-secondary uppercase tracking-widest text-xs">
              {f.label} {f.hint && <span className="text-ink-muted normal-case tracking-normal ml-2">— {f.hint}</span>}
            </Label>
            {f.textarea ? (
              <Textarea
                data-testid={`settings-${f.key}`}
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="bg-navy border-navy-border text-white mt-2 rounded-sm min-h-[80px]"
              />
            ) : (
              <Input
                data-testid={`settings-${f.key}`}
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="bg-navy border-navy-border text-white mt-2 rounded-sm"
              />
            )}
          </div>
        ))}
        <div>
          <Label className="text-ink-secondary uppercase tracking-widest text-xs">Accent color</Label>
          <div className="mt-2 flex items-center gap-3">
            <div className="w-12 h-10 rounded-sm border border-navy-border shrink-0" style={{ background: form.accent_color || "#DC2626" }} />
            <Input data-testid="settings-accent_color" value={form.accent_color || ""} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} placeholder="#DC2626" className="bg-navy border-navy-border text-white rounded-sm font-mono w-40" />
          </div>
        </div>
      </div>

      {/* Featured tournaments buttons */}
      <div className="bg-navy-deep border border-navy-border rounded-sm p-4 md:p-6 mt-6" data-testid="settings-featured-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading uppercase tracking-widest text-orange text-sm flex items-center gap-2">
            <Trophy size={14} /> Torneios em Destaque na Home
          </h3>
          <Button data-testid="add-featured-btn" onClick={addFeatured} className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest text-xs rounded-sm h-8 px-3">
            <Plus size={14} className="mr-1" /> Adicionar
          </Button>
        </div>
        <p className="text-ink-muted text-xs mb-3">Aparecem como grandes botões logo abaixo do Hero da home, direcionando o visitante ao torneio.</p>
        {featured.length === 0 ? (
          <div className="text-ink-muted text-sm py-4 text-center border border-dashed border-navy-border rounded-sm">Nenhum torneio em destaque. Clique em Adicionar.</div>
        ) : (
          <div className="space-y-2">
            {featured.map((f, i) => {
              const t = tournaments.find((x) => x.id === f.tournament_id);
              return (
                <div key={i} className="flex flex-wrap items-center gap-2 bg-navy border border-navy-border rounded-sm p-2" data-testid={`featured-row-${i}`}>
                  <span className="font-mono text-orange w-6 text-center">{i + 1}</span>
                  <select
                    value={f.tournament_id}
                    onChange={(e) => updateFeatured(i, { tournament_id: e.target.value })}
                    data-testid={`featured-tournament-${i}`}
                    className="bg-navy-deep border border-navy-border text-white rounded-sm px-2 py-1 text-sm flex-1 min-w-[160px]"
                  >
                    {tournaments.map((x) => (
                      <option key={x.id} value={x.id}>{x.name}{x.archived ? " (arquivado)" : ""}</option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1">
                    <div className="w-8 h-8 rounded-sm border border-navy-border shrink-0" style={{ background: f.color || "#DC2626" }} />
                    <Input
                      data-testid={`featured-color-${i}`}
                      value={f.color || ""}
                      onChange={(e) => updateFeatured(i, { color: e.target.value })}
                      placeholder="#DC2626"
                      className="bg-navy-deep border-navy-border text-white rounded-sm font-mono text-xs w-24 h-8"
                    />
                  </div>
                  <div className="flex gap-1">
                    <button type="button" data-testid={`featured-up-${i}`} onClick={() => moveFeatured(i, -1)} className="w-7 h-7 grid place-items-center bg-navy-deep border border-navy-border rounded-sm hover:border-orange hover:text-orange"><ArrowUp size={12} /></button>
                    <button type="button" data-testid={`featured-down-${i}`} onClick={() => moveFeatured(i, 1)} className="w-7 h-7 grid place-items-center bg-navy-deep border border-navy-border rounded-sm hover:border-orange hover:text-orange"><ArrowDown size={12} /></button>
                    <button type="button" data-testid={`featured-remove-${i}`} onClick={() => removeFeatured(i)} className="w-7 h-7 grid place-items-center bg-navy-deep border border-navy-border rounded-sm hover:border-court-red hover:text-court-red"><Trash2 size={12} /></button>
                  </div>
                  {t?.slug && <span className="text-[10px] font-mono text-ink-muted">/t/{t.slug}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Block visibility */}
      <div className="bg-navy-deep border border-navy-border rounded-sm p-4 md:p-6 mt-6" data-testid="settings-blocks-section">
        <h3 className="font-heading uppercase tracking-widest text-orange text-sm mb-4">Blocos visíveis na Home</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {BLOCKS.map((b) => {
            const val = form.visible_blocks?.[b.key] !== false;
            return (
              <label key={b.key} className="flex items-center justify-between gap-3 bg-navy border border-navy-border rounded-sm p-3 cursor-pointer" data-testid={`block-toggle-${b.key}`}>
                <span className="font-heading uppercase tracking-widest text-xs text-white">{b.label}</span>
                <Switch checked={val} onCheckedChange={(v) => setBlock(b.key, v)} data-testid={`block-switch-${b.key}`} />
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BackgroundField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-ink-secondary uppercase tracking-widest text-xs">{label}</Label>
      <div className="mt-2 aspect-[16/9] rounded-sm overflow-hidden border border-navy-border bg-navy">
        {value ? (
          <img src={fileUrl(value)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-ink-muted text-xs">No image</div>
        )}
      </div>
      <div className="mt-3 space-y-2">
        <Input
          data-testid={`${testid}-url-input`}
          placeholder="Paste image URL or upload"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="bg-navy border-navy-border text-white rounded-sm font-mono text-xs"
        />
        <ImageUpload value={value} onChange={onChange} label="Or upload a new image" testid={testid} />
      </div>
    </div>
  );
}
